<template>
  <div
    id="search-container"
    class="search-container"
    :style="{ width: width, height: height }"
  >
    <div class="search" :class="disable && 'disable'">
      <input
        type="text"
        class="search-box"
        v-model="state"
        autocomplete="off"
        @input="onTyping"
        :placeholder="placeholder || $t('search-here')"
        @keyup.enter="changeSearch"
        :disabled="disable"
      />
      <button
        @click="clear"
        class="clear-btn pb-1"
        v-tooltip:bottom="'Clear search'"
        v-if="state.length > 0"
        id="btn-clear-search"
      >
        <BaseIcon name="closeButton-mini" />
      </button>
      <button
        class="search-icon"
        :class="disable || !state ? 'button-disabled' : 'button-normal'"
        @click="changeSearch"
        id="btn-search"
      >
        <BaseIcon name="search" class="svg" width="1.219em" height="1.219em" />
      </button>
    </div>
    <ul v-if="filteredStates.length > 0 && onFocus" class="search-list">
      <li
        v-for="(filteredState, index) in filteredStates"
        :key="index"
        @click="onOptionSelection(filteredState)"
        class="items"
        :id="`select-item-${index}`"
      >
        {{ filteredState.label }}
        <!-- <div v-html="highlight(filteredState.label)"></div>
        <span class="typed">
          {{ filteredState.label.substr(0, state.length) }}
        </span>
        {{ filteredState.label.substr(state.length) }} -->
      </li>
    </ul>
    <!-- <div
      v-if="filteredStates.length === 0 && onFocus"
      class="search-list no-list"
    >
      <NoData class="no-items" :message="$t('noData')"></NoData>
    </div> -->
  </div>
</template>

<script>
/**
 * @author: Tarun Rawat
 *
 * Props: Self Explaintory(width ,height,placeholder)
 *        states: auto search options list
 *
 *
 * Events triggered:
 *
 * onTyping: when text is typed
 *  {keyword:value}
 *
 *
 * onOptionSelection: when any option is selected
 * {optionSelected:'xcduicds11321' }
 *
 *
 * search: when search is clicked
 * {'text':'Rakuten'}
 *
 * Imp:
 *
 * It is responsibilty of parent component to provide option to it
 *
 *
 */
import NoData from '@/rex-components/rex-no-data.vue';

export default {
  name: 'RexSearch',
  components: { NoData },
  data: function () {
    return {
      state: '',
      onFocus: false,
      filteredStates: [],
      onTyping: this.debounce(this.fetchAutoSuggestion, this.debounceDelay),
    };
  },
  props: {
    /**
     * holds the suggestions which are shown to user as user types, Array of Objects
     * @example
     * [
        {
          "key": "suggestion_1",
          "label": "Suggestion 1"
        },
        {
          "key": "suggestion_2",
          "label": "Suggestion 2"
        }
       ]
     */
    states: { type: Array, default: null },
    /**
     * placeholder which will be visible inside the searchbox
     */
    placeholder: { type: String },
    /**
     * width of searchbox
     */
    width: {
      type: String,
      default: '30.5em',
    },
    /**
     * height of the Component
     */
    height: {
      type: String,
      default: '2.5em',
    },
    /**
     * how long to wait before making API call for search Auto-Suggestions
     */
    debounceDelay: {
      type: Number,
      default: 500,
    },
    /**
     * whether to clear the search box after search button is clicked
     */
    clearOnSearch: {
      type: Boolean,
      default: false,
    },
    /**
     * whether to disable the search button
     */
    disable: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    fetchAutoSuggestion(event) {
      const { value } = event.target;
      // this.filteredStates = [];

      if (value.trim().length === 0) {
        /**
         * emitted when clear-search icon is clicked or search box is text is made Empty
         * @property {string}
         * @event clearSelection
         */
        this.$emit('clearSelection', '');
        this.onFocus = false;
        return;
      }
      this.onFocus = true;
      /**
       * emitted as user types, typed input is sent in form of object
       * @property {object}
       * @event onTyping
       */
      this.$emit('onTyping', { keyword: value });
    },
    highlight(content) {
      if (!this.state) {
        return content;
      }
      return content.replace(new RegExp(this.state, 'gi'), (match) => {
        return '<span class="highlightText">' + match + '</span>';
      });
    },
    /**
     *  Triggers when ENTER key is pressed.
     * @method onEnterKeyUp
     */
    onEnterKeyUp() {
      /**
       *  emits onEnterKeyUp event when ENTER key is pressed.
       *
       * @event onEnterKeyUp
       *@property {object}
       */
      this.$emit('onEnterKeyUp');
    },
    clear(item) {
      this.state = '';
      this.filteredStates = [];
      this.onFocus = false;
      this.$emit('clearSelection', '');
    },
    onOptionSelection(option) {
      this.state = option.label;
      /**
       * emitted when user selects one of the options which are provided by auto-Suggestion, selected option is sent as an Object
       * @property {object}
       * @event onOptionSelection
       */
      this.$emit('onOptionSelection', { optionSelected: option.key });
      this.onFocus = false;
    },
    changeSearch() {
      if (this.state && !this.disable) {
        /**
         * emitted when search button is clicked
         * @property {object}
         * @event search
         */
        this.$emit('search', { text: this.state });
        if (this.clearOnSearch) {
          this.state = '';
          this.filteredStates = [];
          this.onFocus = false;
        }
      }
    },
    /**
     * This is very crucial piece of code
     * It is implementing debouncing
     * A very crucial concept
     */
    debounce(fn, delay) {
      let timerId;
      return (...args) => {
        if (timerId) clearTimeout(timerId);
        timerId = setTimeout(() => {
          fn.apply(this, args);
        }, delay);
      };
    },
  },

  mounted() {
    /**
     * This is to check if mouse is clicked outside the search box
     * We have to hide search suggestion list in that case
     * Trick is simple
     *
     */
    const specifiedElement = document.getElementById('search-container');
    document.addEventListener('click', (event) => {
      const isClickInside = specifiedElement.contains(event.target);
      if (!isClickInside) {
        this.onFocus = false;
      }
    });
  },
  watch: {
    states: function (newVal, oldVal) {
      //console.log('newVal', newVal);
      this.filteredStates = newVal;
    },
  },
};
</script>

<style lang="scss" scoped>
.highlightText {
  color: $font-primary;
  font-family: $regular !important;
}
.search {
  display: flex;
  flex-direction: row;
  height: 100%;
  color: $font-primary !important;
  ::-webkit-input-placeholder {
    color: $font-primary;
  }
  width: 100%;
}
.search-box {
  background-color: transparent;
  // border-right: none;
  border: none;
  padding: 0.625em;
  height: 100%;
  width: 100%;
  border-radius: 0.4375rem 0rem 0rem 0.4375rem;
  line-height: 1.25em;
  font-size: 1em;
  margin: 0;
  caret-color: $color-brand-accent;
  color: $font-primary;
}
.search-icon {
  background-color: $color-brand-accent;
  cursor: pointer;
  color: $font-contrast !important;
  width: 3.25em;
  border: none;
  margin: 0;
  height: 100%;
  list-style: none;
  // border-left: 0.0313em $border-primary solid;
  // border-left: 0.3px rgb(53, 64, 83) solid;
  // border-left: 0.3px #d3d8c1 solid;
  border-radius: 0rem 0.5375rem 0.5375rem 0rem;
  fill: $font-contrast;
}
.button-disabled {
  cursor: not-allowed !important;
}
.button-normal {
  cursor: pointer !important;
}

.search-list {
  width: 100%;
  background-color: $bg-secondary;
  max-height: 15.6875em;
  border-radius: 0.375em;
  margin: 0;
  overflow-y: scroll;
  cursor: pointer;
  list-style: none;
  @include dropshadow-effect;
  text-align: left;
  z-index: 2;
  position: absolute;
  margin-top: 0.2em;
  padding: 0;
  .items {
    color: $font-primary;
    font-size: 1em;
    font-family: $regular;
    padding: 0em 0.625em;
    min-height: 2.5em;
    display: flex;
    align-items: center;
  }
}
.no-list {
  text-align: center !important;
}
.no-items {
  height: 15.6875em;
  width: 100%;
  color: $font-primary;
  font-size: 1em;
  font-family: $regular;
  padding: 0em 0.625em;
  min-height: 2.5em;
  display: flex;
  align-items: center;
  transform: translate(0%, 0%) !important;
}
.items:hover {
  background-color: $color-brand-accent;
  color: $font-contrast;
}

.search-container {
  position: relative;
  border: 0.0313em $border-primary solid;
  border-radius: 0.5em;
}
.typed {
  background-color: pink;
}
.clear-btn {
  height: 100%;
  background: transparent;
  border: transparent;
  fill: $color-brand-accent;
}

.disable {
  pointer-events: none;
  opacity: 0.4;
}

@media screen and (max-width: 1199px) {
  .search-container {
    font-size: 12px;
  }
}

@media screen and (min-width: 1200px) {
  .search-container {
    font-size: 14px;
  }
}
@media screen and (min-width: 1600px) {
  .search-container {
    font-size: 16px;
  }
}
@media screen and (min-width: 1900px) {
  .search-container {
    font-size: 18px;
  }
}
</style>
